import * as React from "react";
import { CPText, DateText } from "shared/components/base";
import { Chip } from "@progress/kendo-react-buttons";
import { CPGridV2, useCPGridV2 } from "shared/components/shared/CPGridV2";
import type {
  CPGridV2Column,
  CPGridV2FilterOption,
} from "shared/components/shared/CPGridV2";
import "./AdminPendingUsersStyles.scss";
import { GridFetchResponse, ValueObject } from "shared/types";
import UserDetailsModal, {
  ApproveConfirmModal,
  DenyConfirmModal,
  UpdatePendingUserRecord,
} from "./UserDetailsModal";
import useAppState from "shared/state/useAppState";
import http from "shared/http";
import { useQuery, useQueryClient } from "react-query";
import {
  PendingUser,
  useUpsertPendingUserMutation,
} from "data/pendingUser/pendingUser";
import { useAgenciesLookupQuery } from "data/lookup/lookupQueries";

export interface UseGetPendingUsersForGridQueryOptions {
  firstname?: string;
  lastname?: string;
  email?: string;
  agencies?: string;
  requestType?: string;
  status?: "pending" | "approved" | "denied" | null;
  take?: number;
  skip?: number;
  sortBy?: string;
  ascending?: boolean;
}

export const useGetPendingUsersForGridQuery = (
  params: UseGetPendingUsersForGridQueryOptions,
) => {
  return useQuery(
    ["pending-users-grid", JSON.stringify(params)],
    async () => {
      const response: GridFetchResponse = await http.postAsync(
        "admin/pending-users/search",
        params,
      );
      return response;
    },
    {
      staleTime: 30 * 1000, // Cache for 30 seconds
      enabled: true,
    },
  );
};

// COLUMNS
interface ColumnHandlers {
  onViewDetails: (user: UpdatePendingUserRecord) => void;
  onApprove: (user: UpdatePendingUserRecord) => void;
  onDeny: (user: UpdatePendingUserRecord) => void;
}

const createColumns = ({
  onViewDetails,
  onApprove,
  onDeny,
}: ColumnHandlers): CPGridV2Column[] => [
  {
    columnType: "menu",
    onMenuOpen: () => [
      { label: "View Details", icon: "fa-solid fa-eye" },
      // { label: "Approve", icon: "fa-solid fa-check" },
      // { label: "Deny", icon: "fa-solid fa-ban" },
    ],
    onMenuItemClick: (label, dataItem) => {
      switch (label) {
        case "View Details":
          onViewDetails(dataItem as UpdatePendingUserRecord);
          break;
        case "Approve":
          onApprove(dataItem as UpdatePendingUserRecord);
          break;
        case "Deny":
          onDeny(dataItem as UpdatePendingUserRecord);
          break;
        default:
          break;
      }
    },
  },
  { field: "id", title: "ID", width: 70, hide: true, pinned: true },
  {
    field: "firstname",
    title: "First Name",
    width: 200,
    renderCell: (data: ValueObject) => {
      return (
        <CPText style={{ textTransform: "capitalize" }}>
          {data.dataItem.firstname}
        </CPText>
      );
    },
  },
  {
    field: "lastname",
    title: "Last Name",
    width: 200,
    renderCell: (data: ValueObject) => {
      return (
        <CPText style={{ textTransform: "capitalize" }} textStyle={"body"}>
          {data.dataItem.lastname}
        </CPText>
      );
    },
  },
  { field: "email", title: "Email", width: 340 },
  { field: "externalSystemId", title: "Previous Username", width: 200 },
  {
    field: "requestType",
    title: "Request Type",
    width: 200,
    renderCell: (data: ValueObject) => {
      const value: string = data.dataItem.requestType ?? "";
      if (!value) return null;
      return <Chip text={value} size="small" />;
    },
  },
  {
    field: "agencies",
    title: "Agencies",
    width: 240,
    sortable: false,
    renderCell: (data: ValueObject) => {
      const raw: string = data.dataItem.agencies ?? "";
      const items = raw
        ? raw
            .split(",")
            .map((a: string) => a.trim())
            .filter(Boolean)
        : [];
      if (!items.length) return null;
      const visible = items.slice(0, 2);
      const overflow = items.slice(2);
      return (
        <div
          style={{
            display: "flex",
            gap: 3,
            alignItems: "center",
            overflow: "hidden",
            flexWrap: "nowrap",
          }}
        >
          {visible.map((item) => (
            <Chip key={item} text={item} size="small" />
          ))}
          {overflow.length > 0 && (
            <span title={overflow.join(", ")}>
              <Chip text={`+${overflow.length}`} size="small" />
            </span>
          )}
        </div>
      );
    },
  },
  {
    field: "status",
    title: "Status",
    width: 120,
    renderCell: (data: ValueObject) => {
      const status: string = data.dataItem.status ?? "";
      const colorMap: Record<string, "warning" | "success" | "error" | "base"> =
        {
          pending: "warning",
          approved: "success",
          denied: "error",
        };
      const themeColor = colorMap[status.toLowerCase()] ?? "base";
      return <Chip text={status} themeColor={themeColor} size="small" />;
    },
  },
  {
    field: "reason",
    title: "Reason",
    width: 200,
    sortable: false,
    renderCell: (data: ValueObject) => {
      const value: string = data.dataItem.reason ?? "";
      if (!value) return null;
      return (
        <span
          title={value}
          style={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </span>
      );
    },
  },
  { field: "updatedBy", title: "Updated By", width: 200, sortable: false },
  {
    field: "createdAt",
    title: "Created At",
    width: 200,
    renderCell: (data: ValueObject) => (
      <DateText date={data.dataItem.createdAt} />
    ),
  },
  {
    field: "updatedAt",
    title: "Updated At",
    width: 200,
    renderCell: (data: ValueObject) => (
      <DateText date={data.dataItem.updatedAt} />
    ),
  },
];

const toUpdateRecord = (user: PendingUser): UpdatePendingUserRecord => ({
  id: user.id,
  firstname: user.firstname,
  lastname: user.lastname,
  email: user.email,
  roles: [user.requestType ?? ""],
  agencies: Array.isArray(user.agencies)
    ? user.agencies
    : user.agencies
      ? user.agencies.split(",").map((a: string) => a.trim())
      : [],
  isApproved: user.isApproved ?? false,
  reason: user.reason ?? "",
  externalSystemId: user.externalSystemId ?? "",
});

const AdminPendingUsersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { setToastMsg, setIsLoading } = useAppState();
  const { data: agencies } = useAgenciesLookupQuery();
  const [filterParams, setFilterParams] =
    React.useState<UseGetPendingUsersForGridQueryOptions>({
      take: 20,
      skip: 0,
      status: "pending",
    });
  const {
    data: users,
    isFetching: isUsersLoading,
    refetch,
  } = useGetPendingUsersForGridQuery(filterParams);
  const [selectedUser, setSelectedUser] =
    React.useState<UpdatePendingUserRecord | null>(null);
  const [denyingUser, setDenyingUser] =
    React.useState<UpdatePendingUserRecord | null>(null);
  const [approvingUser, setApprovingUser] =
    React.useState<UpdatePendingUserRecord | null>(null);
  const { mutate, isLoading } = useUpsertPendingUserMutation();

  const filterOptions = React.useMemo<CPGridV2FilterOption[]>(
    () => [
      {
        filterType: "text",
        label: "First Name",
        columnKey: "firstname",
        placeholder: "e.g. John",
      },
      {
        filterType: "text",
        label: "Last Name",
        columnKey: "lastname",
        placeholder: "e.g. Smith",
      },
      {
        filterType: "email",
        label: "Email",
        columnKey: "email",
        placeholder: "e.g. john@mail.com",
      },
      {
        filterType: "multicheck",
        label: "Agency",
        columnKey: "agencies",
        placeholder: "Search agency codes...",
        toType: "comma",
        showSelectAllControls: true,
        options:
          agencies?.map((agency) => ({
            label: agency.agency,
            value: agency.agency,
          })) ?? [],
      },
      {
        filterType: "dropdown",
        label: "Request Type",
        columnKey: "requestType",
        options: [
          "Agency Manager / Owner",
          "Agent Employee",
          "Driver",
          "Owner Operator / Fleet Owner",
        ],
      },
      {
        filterType: "dropdown",
        label: "Status",
        columnKey: "status",
        options: [
          { label: "Pending", value: "pending" },
          { label: "Denied", value: "denied" },
        ],
        defaultValue: "pending",
      },
    ],
    [agencies],
  );

  React.useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading]);

  const handleApprove = (user: UpdatePendingUserRecord) => {
    setApprovingUser(toUpdateRecord(user));
  };

  const doApprove = (user: UpdatePendingUserRecord) => {
    mutate(
      { ...user, isApproved: true },
      {
        onSuccess: () => {
          setToastMsg("User approved successfully", "success");
          queryClient.invalidateQueries("pending-users-grid");
        },
        onError: (error: unknown) => {
          setToastMsg("An error occurred during the request.", "error");
        },
      },
    );
  };

  const adapter = useCPGridV2({
    columns: createColumns({
      onViewDetails: setSelectedUser,
      onApprove: handleApprove,
      onDeny: (user) => setDenyingUser(toUpdateRecord(user)),
    }),
    data: users?.records ?? [],
    showLoader: isUsersLoading,
    totalRecords: users?.totalRecords ?? 0,
  });

  return (
    <div className="admin-pending-users-page">
      <CPGridV2
        name="admin-pending-users-grid"
        title="Pending User Requests"
        adapter={adapter}
        compact
        sortable
        pageable
        noWrap
        gridLines="rows"
        filterOptions={filterOptions}
        onApplyFilters={(filters, paging) => {
          setFilterParams({
            ...filters,
            ...paging,
          } as UseGetPendingUsersForGridQueryOptions);
        }}
        onClearFilters={() => setFilterParams({ take: 20, skip: 0 })}
        onRefresh={() => queryClient.invalidateQueries("pending-users-grid")}
      />

      <UserDetailsModal
        key={selectedUser?.id}
        selectedUser={selectedUser}
        onClose={() => setSelectedUser(null)}
        onSave={() => {
          setSelectedUser(null);
          queryClient.invalidateQueries("pending-users-grid");
        }}
      />

      {approvingUser && (
        <ApproveConfirmModal
          firstname={approvingUser.firstname}
          lastname={approvingUser.lastname}
          email={approvingUser.email}
          externalSystemId={approvingUser.externalSystemId}
          onConfirm={() => {
            doApprove(approvingUser);
            setApprovingUser(null);
          }}
          onCancel={() => setApprovingUser(null)}
        />
      )}

      {denyingUser && (
        <DenyConfirmModal
          reason={denyingUser.reason ?? ""}
          onReasonChange={(value) =>
            setDenyingUser((prev) => (prev ? { ...prev, reason: value } : prev))
          }
          onConfirm={() => {
            mutate(
              { ...denyingUser, isApproved: false },
              {
                onSuccess: () => {
                  setToastMsg("User denied successfully", "success");
                  setDenyingUser(null);
                  queryClient.invalidateQueries("pending-users-grid");
                },
                onError: (error: unknown) => {
                  setToastMsg("An error occurred during the request.", "error");
                },
              },
            );
          }}
          onCancel={() => setDenyingUser(null)}
        />
      )}
    </div>
  );
};

export default AdminPendingUsersPage;
